import { state, DOM } from './state.js';
import { ReservationService } from './services/reservation.service.js';
import { navigateTo } from './navigation.js';
import * as Steps from './components/reservas/Steps.js';
import { showToast } from './components/shared/Toast.js';
import {
    getAvailableMomentos,
    getValidTimeSlots,
    generateDateChips,
} from './services/reservation.rules.js';
import { hideDecorativeIcons } from './utils/aria.js';
import { PERSONAS_MIN, PERSONAS_MAX } from './shared/constants.js';

/**
 * Módulo de Reservas v4 - Optimizado (Anticipación 2hs)
 */

const AREAS = [
    { id: 'vereda', name: 'Vereda', icon: 'fa-street-view' },
    { id: 'salon', name: 'Salón', icon: 'fa-chair' }
];

// Caché de disponibilidad para todos los momentos de una (fecha, personas)
// Cacheando toda la jornada de una vez, el cambio de momento es instantáneo.
let allAvailabilityCache = {
    key: null,          // `${fecha}-${personas}`
    byMomento: {}       // { [momento]: { [slot]: [{id, name, disponible}] } }
};

let loadingIconInterval = null;

export function initReservas() {
    if (window.location.pathname === '/reservas') {
        renderReservaStep(state.reserva.step || 1);
    }
}

/**
 * Motor de Render de Pasos
 */
export async function renderReservaStep(step) {
    if (!DOM.reservaWizardContainer) return;
    if (loadingIconInterval) { clearInterval(loadingIconInterval); loadingIconInterval = null; }
    const prevStep = state.reserva.step;
    state.reserva.step = step;

    let html = '';

    switch(step) {
        case 1: 
            html = Steps.Step1(state.reserva); 
            break;
        case 2: 
            html = Steps.Step2(state.reserva, generateDateChips(new Date())); 
            break;
        case 3: 
            html = await renderStep3Logic(); 
            break;
        case 4: 
            html = Steps.Step4(state.reserva); 
            break;
        case 5: 
            html = renderStep5Logic(); 
            break;
    }

    DOM.reservaWizardContainer.innerHTML = html;
    hideDecorativeIcons(DOM.reservaWizardContainer);
    setupStepListeners(step);

    // Reset del scroll al TOPE cuando cambiamos de paso. En mobile el foco de
    // cada input dispara autoscroll (por el teclado), dejando la ventana abajo;
    // sin esto el paso siguiente aparece scrolleado (ej. Step 2 mostrando solo
    // la 2da fila de días). Solo en cambio de paso — NO en re-renders del mismo
    // paso (adj-personas, set-hora, set-area) para no dar saltos molestos.
    //
    // Es a prueba de balas porque el scroller varía según el layout/dispositivo:
    // 1) blur() cierra el teclado (su foco es lo que desplaza el viewport visual);
    // 2) reseteamos scroll en window + documentElement + body + main-content
    //    (el shell usa main overflow-hidden, pero según la altura el offset
    //    puede quedar en window/documentElement). Resetear un no-scroller es no-op.
    if (step !== prevStep) {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        window.scrollTo({ top: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        const mainContent = document.getElementById('main-content');
        if (mainContent) mainContent.scrollTop = 0;
    }
}

async function renderStep3Logic() {
    const selDate = new Date(`${state.reserva.fecha}T12:00:00`);
    const availableMomentos = getAvailableMomentos(selDate);

    if (!availableMomentos.includes(state.reserva.momento)) {
        state.reserva.momento = availableMomentos[0];
    }
    if (!state.reserva.momento) state.reserva.momento = availableMomentos[0];

    const fecha = state.reserva.fecha;
    const personas = state.reserva.personas;
    const momento = state.reserva.momento;
    const cacheKey = `${fecha}-${personas}`;

    if (allAvailabilityCache.key !== cacheKey) {
        // Cache fría: mostrar pantalla de carga y prefetchear todos los momentos en paralelo
        setTimeout(async () => {
            try {
                const byMomento = {};
                await Promise.all(availableMomentos.map(async m => {
                    const slots = getValidTimeSlots(fecha, m);
                    const slotResults = {};
                    await Promise.all(slots.map(async s => {
                        slotResults[s] = await Promise.all(AREAS.map(async a => {
                            const disp = await ReservationService.checkDisponibilidad(fecha, m, a.name, personas);
                            return { ...a, disponible: disp.disponible };
                        }));
                    }));
                    byMomento[m] = slotResults;
                }));
                allAvailabilityCache = { key: cacheKey, byMomento };
                if (state.reserva.fecha === fecha && state.reserva.personas === personas) {
                    renderReservaStep(3);
                }
            } catch (_e) {}
        }, 0);
        return Steps.StepLoading();
    }

    // Cache caliente: render instantáneo para cualquier momento
    const timeValidSlots = getValidTimeSlots(fecha, momento);
    const momentoData = allAvailabilityCache.byMomento[momento] || {};
    const availableSlots = timeValidSlots.filter(s => (momentoData[s] || []).some(a => a.disponible));
    const slotsHtml = renderSlotsMarkup(availableSlots, state.reserva.hora);
    const areasHtml = state.reserva.hora ? renderAreasMarkup(momentoData[state.reserva.hora] || []) : '';

    return Steps.Step3(state.reserva, availableMomentos, slotsHtml, areasHtml);
}

function renderSlotsMarkup(slots, selectedHora) {
    if (slots.length === 0) return `<p class="col-span-3 text-[10px] font-bold text-text-muted uppercase italic py-4 text-center">Sin disponibilidad para este turno.</p>`;

    return slots.map(s => `
        <button data-action="set-hora" data-value="${s}"
            role="radio" aria-checked="${selectedHora === s}"
            class="p-4 rounded-xl border-2 font-black text-sm transition-all ${selectedHora === s ? 'bg-invert-bg border-invert-bg text-invert-text shadow-lg' : 'bg-base border-border-subtle text-text-primary'}">
            ${s}
        </button>
    `).join('');
}

function renderAreasMarkup(ocuResults) {
    const availableAreas = ocuResults.filter(a => a.disponible);
    return `
        <div class="mt-4 animate-fade-in">
            <div role="radiogroup" aria-label="Elegí el sector">
                <p id="area-label" class="text-[10px] font-black text-text-muted uppercase tracking-widest block mb-2 ml-4">Sector disponible</p>
                <div class="flex flex-col gap-2">
                    ${availableAreas.length > 0 ? availableAreas.map(a => `
                        <button data-action="set-area" data-value="${a.name}"
                            role="radio" aria-checked="${state.reserva.area === a.name}"
                            class="p-4 rounded-2xl border-2 transition-all text-left flex items-center justify-between ${state.reserva.area === a.name ? 'border-brand bg-brand/5 shadow-sm' : 'border-border-subtle bg-surface'}">
                            <span class="text-sm font-black uppercase italic">${a.name}</span>
                            <i class="fa-solid ${state.reserva.area === a.name ? 'fa-circle-check text-brand' : 'fa-chevron-right text-[10px] opacity-20'}" aria-hidden="true"></i>
                        </button>
                    `).join('') : `
                        <p class="text-[10px] font-bold text-red-500 uppercase italic p-5 bg-red-50 rounded-2xl">Sin capacidad en este turno.</p>
                    `}
                </div>
            </div>
        </div>
    `;
}

function renderStep5Logic() {
    return Steps.Step5(state.reserva);
}

function setupStepListeners(step) {
    const container = DOM.reservaWizardContainer;
    if (!container) return;

    container.querySelectorAll('[data-action]').forEach(el => {
        el.addEventListener('click', async () => {
            const action = el.dataset.action;
            const val = el.dataset.value;

            switch(action) {
                case 'adj-personas': {
                    const newVal = state.reserva.personas + parseInt(el.dataset.val, 10);
                    if (newVal >= PERSONAS_MIN && newVal <= PERSONAS_MAX) {
                        // Persistimos TODO lo tipeado antes de re-renderizar el
                        // Step 1: los inputs se recrean desde `state.reserva`, así
                        // que cualquier campo no guardado acá se pierde. Incluye
                        // teléfono (código de área + celular), no solo nombre.
                        const inputNombre = document.getElementById('inputNombre');
                        const inputApellido = document.getElementById('inputApellido');
                        const inputTelCodigo = document.getElementById('inputTelCodigo');
                        const inputTelNumero = document.getElementById('inputTelNumero');
                        if (inputNombre) state.reserva.nombre = inputNombre.value;
                        if (inputApellido) state.reserva.apellido = inputApellido.value;
                        if (inputTelCodigo) state.reserva.telCodigo = inputTelCodigo.value;
                        if (inputTelNumero) state.reserva.telNumero = inputTelNumero.value;

                        state.reserva.personas = newVal;
                        renderReservaStep(1);
                    }
                    break;
                }
                case 'set-fecha':
                    state.reserva.fecha = val;
                    state.reserva.hora = '';
                    state.reserva.area = '';
                    renderReservaStep(3);
                    break;
                case 'set-momento':
                    if (state.reserva.momento === val) break; // ya activo, no hacer nada
                    state.reserva.momento = val;
                    state.reserva.hora = '';
                    state.reserva.area = '';
                    renderReservaStep(3);
                    break;
                case 'set-hora':
                    state.reserva.hora = val;
                    state.reserva.area = '';
                    renderReservaStep(3);
                    break;
                case 'set-area':
                    state.reserva.area = val;
                    renderReservaStep(3);
                    break;
                case 'reserva-next':
                    if (step === 1) {
                        const name = document.getElementById('inputNombre').value.trim();
                        const lname = document.getElementById('inputApellido').value.trim();
                        const codigo = document.getElementById('inputTelCodigo').value.trim();
                        const numero = document.getElementById('inputTelNumero').value.trim();
                        if (!name || !lname) { showToast("Por favor completá nombre y apellido.", { level: 'warning' }); return; }
                        if (!codigo || !numero) { showToast("Por favor completá tu número de celular.", { level: 'warning' }); return; }
                        state.reserva.nombre = name;
                        state.reserva.apellido = lname;
                        state.reserva.telCodigo = codigo;
                        state.reserva.telNumero = numero;
                        // Armamos el `telefono` completo con los prefijos fijos (0 del
                        // código + 15 del celular) que se muestran fuera de los campos.
                        // Este es el campo que el backend (api/reservas.js) realmente
                        // persiste — antes NO se armaba, así que el teléfono del cliente
                        // se guardaba en null. Ver telCodigo/telNumero (partes crudas).
                        state.reserva.telefono = `0${codigo} 15${numero}`;
                    }
                    renderReservaStep(step + 1);
                    break;
                case 'reserva-back':
                    renderReservaStep(step - 1);
                    break;
                case 'reserva-finish':
                    finishReserva();
                    break;
                case 'reserva-reset':
                    resetReservaState();
                    navigateTo('/');
                    break;
            }
        });
    });

    // Ciclar icono en la pantalla de carga
    const loadingIconEl = document.getElementById('reservaLoadingIcon');
    if (loadingIconEl) {
        const icons = ['fa-utensils', 'fa-wine-glass', 'fa-pizza-slice', 'fa-burger', 'fa-mug-hot', 'fa-ice-cream'];
        let i = 0;
        loadingIconInterval = setInterval(() => {
            i = (i + 1) % icons.length;
            const el = document.getElementById('reservaLoadingIcon');
            if (el) el.className = `fa-solid ${icons[i]} text-3xl text-brand`;
            else { clearInterval(loadingIconInterval); loadingIconInterval = null; }
        }, 700);
    }
}

function resetReservaState() {
    allAvailabilityCache = { key: null, byMomento: {} };
    state.reserva = {
        step: 1,
        personas: 2,
        nombre: '',
        apellido: '',
        telCodigo: '',
        telNumero: '',
        telefono: '',
        fecha: '',
        momento: '',
        hora: '',
        area: '',
        codigo: ''
    };
}

async function finishReserva() {
    const btn = document.getElementById('btnFinalizar');
    if (!btn) return;
    
    btn.disabled = true;
    btn.innerHTML = `<i class="fa-solid fa-circle-notch animate-spin text-lg"></i>`;

    state.reserva.codigo = `PN-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;

    try {
        await ReservationService.book(state.reserva);
        renderReservaStep(5);
    } catch (e) {
        showToast(`No pudimos guardar la reserva: ${e.message}`, { level: 'error', duration: 6000 });
        btn.disabled = false;
        btn.textContent = "Confirmar Reserva";
    }
}
